"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "./LocaleLink";
import { useLocale } from "./LocaleProvider";

const copy = {
  en: {
    title: "Manage Cookie Consent",
    closeLabel: "Close cookie consent",
    body: "To provide the best experiences, we use technologies like cookies to store and/or access device information. Consenting to these technologies will allow us to process data such as browsing behaviour or unique IDs on this site. Not consenting or withdrawing consent may adversely affect certain features and functions.",
    functional: "Functional (Always Active)",
    functionalDesc: "Required for core site functionality and security.",
    on: "ON",
    analytics: "Analytics",
    analyticsDesc: "Helps us understand how visitors use the site (e.g., GA4).",
    marketing: "Marketing",
    marketingDesc: "Used for embedded content and marketing features (e.g., YouTube, Maps).",
    save: "Save preferences",
    accept: "ACCEPT",
    deny: "DENY",
    view: "VIEW PREFERENCES",
    cookiePolicy: "Cookie Policy",
    privacyPolicy: "Privacy Policy",
    note: "You can update your preferences any time from the Cookie Policy page.",
  },
  ro: {
    title: "Gestionați consimțământul pentru cookie-uri",
    closeLabel: "Închide fereastra de consimțământ",
    body: "Pentru a vă oferi cea mai bună experiență, folosim tehnologii precum cookie-urile pentru a stoca și/sau accesa informații de pe dispozitiv. Acordul pentru aceste tehnologii ne permite să prelucrăm date precum comportamentul de navigare sau identificatori unici de pe acest site. Lipsa acordului sau retragerea acestuia poate afecta negativ anumite funcții.",
    functional: "Funcționale (mereu active)",
    functionalDesc: "Necesare pentru funcționarea de bază și securitatea site-ului.",
    on: "ACTIV",
    analytics: "Analitice",
    analyticsDesc: "Ne ajută să înțelegem cum folosesc vizitatorii site-ul (de ex. GA4).",
    marketing: "Marketing",
    marketingDesc: "Folosite pentru conținut încorporat și funcții de marketing (de ex. YouTube, Hărți).",
    save: "Salvează preferințele",
    accept: "ACCEPT",
    deny: "REFUZ",
    view: "VEZI PREFERINȚELE",
    cookiePolicy: "Politica de cookie-uri",
    privacyPolicy: "Politica de confidențialitate",
    note: "Vă puteți modifica preferințele oricând din pagina Politica de cookie-uri.",
  },
} as const;

type ConsentState = {
  functional: true; // always true
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
};

const STORAGE_KEY = "egodent_cookie_consent_v1";

function safeParse(json: string | null): ConsentState | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (
      parsed &&
      parsed.functional === true &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.marketing === "boolean"
    ) {
      return parsed as ConsentState;
    }
    return null;
  } catch {
    return null;
  }
}

export default function CookieConsentModal() {
  const t = copy[useLocale()];
  const [isOpen, setIsOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  // default toggles (only used if user opens preferences before choosing)
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  const existingConsent = useMemo(() => {
    if (typeof window === "undefined") return null;
    return safeParse(localStorage.getItem(STORAGE_KEY));
  }, []);

  useEffect(() => {
    // Show modal ONLY if no consent stored
    const consent = safeParse(localStorage.getItem(STORAGE_KEY));
    if (!consent) setIsOpen(true);
  }, []);

  const saveConsent = (next: Omit<ConsentState, "timestamp">) => {
    const payload: ConsentState = {
      ...next,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setIsOpen(false);
    setPrefsOpen(false);

    // notify app (GA4/YouTube conditional loading can hook into this)
    window.dispatchEvent(new CustomEvent("egodent-cookie-consent", { detail: payload }));
  };

  const acceptAll = () =>
    saveConsent({ functional: true, analytics: true, marketing: true });

  const denyAll = () =>
    saveConsent({ functional: true, analytics: false, marketing: false });

  const savePreferences = () =>
    saveConsent({ functional: true, analytics, marketing });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative mx-auto flex min-h-full max-w-4xl items-center justify-center p-4">
        <div className="w-full rounded-2xl border border-white/10 bg-black/90 shadow-[0_0_40px_rgba(0,0,0,0.8)]">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-6 py-5">
            <h2 className="text-base md:text-lg font-semibold text-white">
              {t.title}
            </h2>
            <button
              aria-label={t.closeLabel}
              onClick={() => setIsOpen(false)}
              className="rounded-full p-2 text-white/70 hover:text-white hover:bg-white/10 transition"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            <p className="text-sm md:text-base text-white/75 leading-relaxed">
              {t.body}
            </p>

            {/* Preferences (optional panel) */}
            {prefsOpen && (
              <div className="mt-5 rounded-2xl border border-[#27E8A7]/25 bg-black/60 p-4">
                <div className="space-y-4">
                  {/* Functional (always on) */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {t.functional}
                      </p>
                      <p className="text-xs text-white/60">
                        {t.functionalDesc}
                      </p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/70">
                      {t.on}
                    </span>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-white">{t.analytics}</p>
                      <p className="text-xs text-white/60">
                        {t.analyticsDesc}
                      </p>
                    </div>
                    <button
                      onClick={() => setAnalytics((v) => !v)}
                      className={[
                        "h-8 w-14 rounded-full border transition relative",
                        analytics
                          ? "bg-[#27E8A7]/20 border-[#27E8A7]/60"
                          : "bg-white/5 border-white/15",
                      ].join(" ")}
                      aria-pressed={analytics}
                    >
                      <span
                        className={[
                          "absolute top-1/2 -translate-y-1/2 h-6 w-6 rounded-full transition",
                          analytics
                            ? "left-7 bg-[#27E8A7]"
                            : "left-1 bg-white/40",
                        ].join(" ")}
                      />
                    </button>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-white">{t.marketing}</p>
                      <p className="text-xs text-white/60">
                        {t.marketingDesc}
                      </p>
                    </div>
                    <button
                      onClick={() => setMarketing((v) => !v)}
                      className={[
                        "h-8 w-14 rounded-full border transition relative",
                        marketing
                          ? "bg-[#27E8A7]/20 border-[#27E8A7]/60"
                          : "bg-white/5 border-white/15",
                      ].join(" ")}
                      aria-pressed={marketing}
                    >
                      <span
                        className={[
                          "absolute top-1/2 -translate-y-1/2 h-6 w-6 rounded-full transition",
                          marketing
                            ? "left-7 bg-[#27E8A7]"
                            : "left-1 bg-white/40",
                        ].join(" ")}
                      />
                    </button>
                  </div>
                </div>

                <button
                  onClick={savePreferences}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-[#27E8A7] px-5 py-2.5 text-sm font-semibold text-black hover:opacity-90 transition"
                >
                  {t.save}
                </button>
              </div>
            )}

            {/* Buttons */}
            <div className="mt-6 grid gap-3 md:grid-cols-3">
              <button
                onClick={acceptAll}
                className="rounded-xl bg-[#27E8A7] px-5 py-3 text-sm font-semibold text-black hover:opacity-90 transition"
              >
                {t.accept}
              </button>

              <button
                onClick={denyAll}
                className="rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition"
              >
                {t.deny}
              </button>

              <button
                onClick={() => setPrefsOpen((v) => !v)}
                className="rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition"
              >
                {t.view}
              </button>
            </div>

            {/* Links */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm">
              <Link
                href="/cookie-policy"
                className="text-[#27E8A7] underline underline-offset-4 hover:text-white transition"
              >
                {t.cookiePolicy}
              </Link>
              <Link
                href="/privacy"
                className="text-[#27E8A7] underline underline-offset-4 hover:text-white transition"
              >
                {t.privacyPolicy}
              </Link>
            </div>

            {/* Tiny note */}
            <p className="mt-4 text-center text-xs text-white/45">
              {t.note}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
